import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    // -----------------------------
    // 1. Attach video sources
    // -----------------------------
    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) {
        source.setAttribute('src', videoSource);
      }
    }

    // -----------------------------
    // 2. Load video
    // -----------------------------
    videoElement.load();

    // -----------------------------
    // 3. Force autoplay settings
    // -----------------------------
    videoElement.muted = true;
    videoElement.playsInline = true;
    videoElement.setAttribute('muted', '');
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('webkit-playsinline', '');
    videoElement.setAttribute('autoplay', '');
    videoElement.setAttribute('loop', '');

    // -----------------------------
    // 4. Autoplay handler (robust)
    // -----------------------------
    const playVideo = async () => {
      try {
        await videoElement.play();
      } catch (err) {
        // Autoplay blocked (ignore silently)
      }
    };

    // Try immediately
    playVideo();

    // Retry when video is ready
    videoElement.addEventListener('loadeddata', playVideo);
    videoElement.addEventListener('canplay', playVideo);
    videoElement.addEventListener('loadedmetadata', playVideo);

    // -----------------------------
    // 5. iOS Safari safety net
    // -----------------------------
    const iosPlayFix = () => {
      videoElement.play().catch(() => {});
    };

    document.addEventListener('touchstart', iosPlayFix, { once: true });
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}